import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { EReportingStatus } from 'constants/reporting.constants';
import _ from 'lodash';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type { TReportingLog } from 'types/reporting.type';
import type {
  IDefaultAPIResult,
  IDefaultAPIState,
} from 'types/state.interface';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import api from '../services/api.service';

type TReportingLogsState = Omit<IDefaultAPIState<TReportingLog>, 'limit'> & {
  totalResults: number;
};

export const initialState: TReportingLogsState = {
  loading: false,
  hasErrors: false,
  results: [],
  map: {},
  page: 0,
  totalPages: 0,
  totalResults: 0,
};

// Slice
const slice = createSlice({
  name: 'reportingLogs',
  initialState,
  reducers: {
    fetchLogsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchLogsSuccess: (
      state,
      {
        payload,
      }: PayloadAction<
        IDefaultAPIResult<TReportingLog> & {
          totalResults: number;
        }
      >
    ) => {
      state.totalResults = payload.totalResults;
      state.totalPages = payload.totalPages;
      state.results = payload.results;
      state.page = payload.page;
      finishedLoadingSuccess(state);
    },
    fetchLogsFailure: (state) => {
      finishedLoadingFailure(state);
    },
  },
});

export default slice.reducer;

// Selectors
export const reportingLogsSelector = (state: TRootState) => {
  return state.reportingLogs.results;
};
export const totalResultsSelector = (state: TRootState) => {
  return state.reportingLogs.totalResults;
};

// Actions
export const { fetchLogsRequest, fetchLogsSuccess, fetchLogsFailure } =
  slice.actions;

type FetchReportingLogsOptions = {
  templateId?: string;
  page?: number;
  limit?: number;
  status?: EReportingStatus;
};

export const fetchReportingLogs = createAppAsyncThunk<
  void,
  FetchReportingLogsOptions
>(
  'reportingLogs/fetchLogs',
  async ({ templateId, page = 1, limit = 10, status }, { dispatch }) => {
    try {
      const query = new URLSearchParams(
        _.omitBy({ page, limit, status, templateId }, _.isNil)
      );
      dispatch(fetchLogsRequest());
      const result = await api.get({
        endpoint: `/reporting/template-settings/logs?${query.toString()}`,
      });
      dispatch(fetchLogsSuccess(result));
    } catch (e) {
      dispatch(fetchLogsFailure());
      console.error(e);
    }
  }
);
